import { useEffect, useMemo, useState } from "react";
import { api } from "../lib/api.js";
import { Modal, PageSpinner } from "./ui.js";

interface Exercise { ref: string; name: string; category: string | null; muscleGroup: string | null; equipment: string | null; allowed: boolean }

export function ExercisePicker({
  onClose,
  onPick,
  title = "Add exercises"
}: {
  onClose: () => void;
  onPick: (picked: { exerciseRef: string; name: string }[]) => void;
  title?: string;
}) {
  const [exercises, setExercises] = useState<Exercise[] | null>(null);
  const [selected, setSelected] = useState<Record<string, string>>({}); // ref -> name
  const [query, setQuery] = useState("");

  useEffect(() => {
    api.get<{ exercises: Exercise[] }>("/api/training/exercises").then((r) => setExercises(r.exercises)).catch(() => setExercises([]));
  }, []);

  const filtered = useMemo(() => {
    if (!exercises) return [];
    const q = query.trim().toLowerCase();
    return exercises.filter((e) => !q || e.name.toLowerCase().includes(q) || (e.muscleGroup ?? "").toLowerCase().includes(q));
  }, [exercises, query]);

  function toggle(e: Exercise) {
    if (!e.allowed) return;
    setSelected((prev) => {
      const next = { ...prev };
      if (next[e.ref]) delete next[e.ref];
      else next[e.ref] = e.name;
      return next;
    });
  }

  const count = Object.keys(selected).length;

  return (
    <Modal title={title} onClose={onClose}>
      <input className="input" placeholder="Search exercises…" value={query} onChange={(e) => setQuery(e.target.value)} />
      <div style={{ maxHeight: "48vh", overflowY: "auto", margin: "var(--space-3) 0" }}>
        {!exercises ? (
          <PageSpinner />
        ) : (
          filtered.map((e) => (
            <button
              key={e.ref}
              type="button"
              className="exercise-row"
              onClick={() => toggle(e)}
              disabled={!e.allowed}
              style={{ opacity: e.allowed ? 1 : 0.4, cursor: e.allowed ? "pointer" : "not-allowed" }}
            >
              <span className={`task-check${selected[e.ref] ? " done" : ""}`} style={{ width: 24, height: 24 }}>{selected[e.ref] ? "✓" : ""}</span>
              <span className="grow" style={{ textAlign: "left" }}>
                <span style={{ fontWeight: 600 }}>{e.name}</span>
                <span className="dim text-xs"> · {e.muscleGroup}{e.equipment ? ` · ${e.equipment}` : ""}</span>
                {!e.allowed && <span className="dim text-xs"> · restricted now</span>}
              </span>
            </button>
          ))
        )}
      </div>
      <button className="btn btn-primary btn-block" disabled={count === 0} onClick={() => onPick(Object.entries(selected).map(([exerciseRef, name]) => ({ exerciseRef, name })))}>
        Add {count > 0 ? count : ""} exercise{count === 1 ? "" : "s"}
      </button>
    </Modal>
  );
}
